import { useOrg } from '@/contexts/OrgContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipTrigger,
} from '@beacon/ui/components';

export function SidebarOrgSwitcher({ collapsed }: { collapsed?: boolean }) {
  const { currentOrg, memberships, switchOrg } = useOrg();

  const hasMultipleOrgs = memberships.length >= 2;
  const initial = currentOrg?.tenantName?.[0]?.toUpperCase() ?? '?';

  if (collapsed) {
    const chip = (
      <span className="bg-primary-9 grid h-[30px] w-[30px] place-items-center rounded font-mono text-xs font-bold text-white">
        {initial}
      </span>
    );

    return (
      <div className="flex justify-center pt-3 pb-3">
        {hasMultipleOrgs ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button aria-label={`Workspace: ${currentOrg?.tenantName}`}>{chip}</button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="start" className="w-52">
              {memberships.map((m) => (
                <DropdownMenuItem
                  key={m.tenantId}
                  onSelect={() => switchOrg(m.tenantSlug)}
                  className={m.tenantSlug === currentOrg?.tenantSlug ? 'font-semibold' : ''}
                >
                  {m.tenantName}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>{chip}</div>
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent side="right">{currentOrg?.tenantName}</TooltipContent>
            </TooltipPortal>
          </Tooltip>
        )}
      </div>
    );
  }

  const trigger = (
    <button className="bg-surface-2 border-stroke-1 flex w-full items-center gap-2.5 rounded border px-2.5 py-2 text-left">
      <span className="bg-primary-9 grid h-[26px] w-[26px] shrink-0 place-items-center rounded font-mono text-xs font-bold text-white">
        {initial}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-text-3 text-3xs font-mono font-semibold tracking-[.12em]">TalentOS</div>
        <div className="text-text-1 truncate text-xs leading-tight font-semibold">{currentOrg?.tenantName}</div>
      </div>
      {hasMultipleOrgs && <Icon symbol="expand-up-down-line" className="text-text-3 h-3.5 w-3.5 shrink-0" />}
    </button>
  );

  return (
    <div className="border-stroke-1 border-b p-2.5">
      {hasMultipleOrgs ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
          <DropdownMenuContent side="bottom" align="start" className="w-52">
            {memberships.map((m) => (
              <DropdownMenuItem
                key={m.tenantId}
                onSelect={() => switchOrg(m.tenantSlug)}
                className={m.tenantSlug === currentOrg?.tenantSlug ? 'font-semibold' : ''}
              >
                {m.tenantName}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        trigger
      )}
    </div>
  );
}
